import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import NavigationMenu from '../components/common/NavigationMenu'
import CountrySelection from '../components/common/country-selection'
import Loader from '../components/common/loader'
import { GetDynamicNavigation } from '../services/contentStack'
import { NAVIGATION_TYPE, ROUTES } from '../utils/constants/enum'
import {
    conditionalClasses,
    transformToNavigationLink,
} from '../utils/helper-functions'
import { useSticky } from '../utils/hooks/useSticky'
import {
    SingleGroupLink,
    SingleGroupLinkItem,
} from '../utils/types/ContentStack'
import { FooterNavType, ForProfessionalSubNav, Nav, NavItem } from './_nav'

import { ReactComponent as DownCarot } from '../assets/images/icons/down.svg'
import beautyHealthlogo from '../assets/images/logo/beauty_health_logo.png'
import logo from '../assets/images/logo/hydrafacial_trademark.png'
import { CLICK_ID } from '../utils/constants/enum'

import SearchBar from '../components/search-bar'
import { NAVIGATION_TYPE_CUSTOMER_GROUP } from '../utils/constants'
import './header.scss'

const Header = () => {
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [expanded, setExpanded] = useState<string | false>(false)
    const [navigation, setNavigation] = useState<Array<Nav>>([])
    const [mobileNav, setMobileNav] = useState<Array<FooterNavType>>([])
    const [isLoading, setLoading] = useState<boolean>(true)
    const { sticky, stickyRef } = useSticky()

    const location = useLocation()
    const navigate = useNavigate()
    const isCareerPage = location.pathname === ROUTES.CAREERS
    const pattern = NAVIGATION_TYPE_CUSTOMER_GROUP.map((path) => {
        return path.replace(/:[^\s/]+/g, '[^\\s/]+').replace(/\//g, '\\/')
    }).join('|')

    const regex = new RegExp(`^(${pattern})($|\/)`, 'i')

    const isCustomerNavigation = regex.test(location.pathname)

    const getData = useCallback(
        async (type) => {
            try {
                let navigation
                if (type === 'Mobile') {
                    if (isCareerPage) {
                        navigation = NAVIGATION_TYPE.CAREER
                    } else if (isCustomerNavigation) {
                        navigation = NAVIGATION_TYPE.CUSTOMER
                    } else {
                        navigation = NAVIGATION_TYPE.HEADER
                    }
                } else {
                    navigation = NAVIGATION_TYPE.MOBILE_MENU
                }
                const res = await GetDynamicNavigation(navigation)
                if (res) {
                    if (type === 'Mobile') {
                        let NavArr
                        if (isCustomerNavigation) {
                            NavArr = res?.navigation?.map((e) => {
                                return transformToNavigationLink(e)
                            })
                        } else if (isCareerPage) {
                            NavArr = res?.main_navigation?.map((e) => {
                                return transformToNavigationLink(e)
                            })
                        } else NavArr = [] as any
                        setNavigation(NavArr)
                    } else {
                        const MobileNavArr = res?.group_navigation?.map(
                            (e: SingleGroupLink) => {
                                return {
                                    click_id: e.click_id,
                                    heading: e.title,
                                    navs: e.group.map(
                                        (item: SingleGroupLinkItem) =>
                                            transformToNavigationLink(item)
                                    ),
                                }
                            }
                        )
                        setMobileNav(MobileNavArr)
                    }
                }
            } catch (error) {
                // error handling
            } finally {
                setLoading(false)
            }
        },
        [isCareerPage, isCustomerNavigation]
    )

    useEffect(() => {
        getData('Mobile').catch((error) => console.log(error))
        getData('Desktop').catch((error) => console.log(error))
    }, [getData, location.pathname])

    const headerClasses = ` ${sticky ? 'sticky' : ''}`

    const handleChange = useCallback(
        (panel: string) =>
            (event: React.SyntheticEvent, isExpanded: boolean) => {
                setExpanded(isExpanded ? panel : false)
            },
        []
    )

    const checkProfessionalNav = (nav: string | undefined, path: string) => {
        const pathMatched = ForProfessionalSubNav.find((d) => d.link === path)
        if (nav === path) {
            return true
        } else if (ROUTES.FOR_PROFESSIONAL === nav && pathMatched) {
            return true
        } else {
            return false
        }
    }

    const activeLinkExpand = () => {
        mobileNav?.map((colValue) => {
            const selectedNav = colValue?.navs.find(
                (d: NavItem) => d.link === location.pathname
            )
            if (selectedNav) {
                setExpanded(colValue?.heading)
            }
        })
    }

    const onLogoClick = () => {
        setMobileMenuOpen(false)
        setExpanded(mobileNav[0]?.heading)
        if (!isCareerPage) {
            navigate('/')
        }
    }

    const navigateToFindAHydrafacialist = () => {
        setMobileMenuOpen(false)
        navigate(ROUTES.FIND_HYDRAFACIALIST)
    }
    const handleNavigate = (link: string) => {
        setMobileMenuOpen(false)
        navigate(link)
    }
    useEffect(() => {
        const bodyEl = document.getElementsByTagName('body')[0]
        if (isMobileMenuOpen) {
            bodyEl.classList.add('menu-open')
        } else {
            bodyEl.classList.remove('menu-open')
        }
    }, [isMobileMenuOpen])

    useEffect(() => {
        activeLinkExpand()
        if (location?.pathname === '/') {
            setExpanded(mobileNav[0]?.heading)
        }

        // eslint-disable-next-line
    }, [])

    const handleMobileMenuOpen = useCallback(() => {
        setMobileMenuOpen(!isMobileMenuOpen)
    }, [isMobileMenuOpen])
    const handleMobileMenuOpenAccordion = useCallback(() => {
        setMobileMenuOpen(false)
    }, [setMobileMenuOpen])

    if (isLoading) {
        return <Loader />
    }
    return (
        <header ref={stickyRef} className={headerClasses}>
            <div className="nav-container">
                <nav>
                    <div
                        className={conditionalClasses(
                            isMobileMenuOpen,
                            'header-mobile',
                            '',
                            'left-panel'
                        )}
                    >
                        <div className="header-logo">
                            <a onClick={onLogoClick}>
                                <img
                                    src={isCareerPage ? beautyHealthlogo : logo}
                                    alt="Hydrafacial Logo"
                                    className={
                                        isCareerPage ? 'beauty-logo' : 'logo'
                                    }
                                />
                            </a>
                        </div>
                        <button
                            className={conditionalClasses(
                                isMobileMenuOpen,
                                'opened',
                                '',
                                'menu hide-desktop'
                            )}
                            aria-label="Main Menu"
                            id={CLICK_ID.HAMBURGERMENU_CLICK}
                            onClick={handleMobileMenuOpen}
                        >
                            <svg width="32" height="32" viewBox="0 0 100 100">
                                <path
                                    className="line line1"
                                    d="M 20,29.000046 H 80.000231 C 80.000231,29.000046 94.498839,28.817352 94.532987,66.711331 94.543142,77.980673 90.966081,81.670246 85.259173,81.668997 79.552261,81.667751 75.000211,74.999942 75.000211,74.999942 L 25.000021,25.000058"
                                />
                                <path className="line line2" d="M 20,47 H 80" />
                                <path
                                    className="line line3"
                                    d="M 20,66.999954 H 80.000231 C 80.000231,70.999954 94.498839,71.182648 94.532987,33.288669 94.543142,22.019327 90.966081,18.329754 85.259173,18.331003 79.552261,18.332249 75.000211,25.000058 75.000211,25.000058 L 25.000021,74.999942"
                                />
                            </svg>
                        </button>
                        <ul className="right-nav hide-tablet-mobile">
                            {navigation?.map((nav) => (
                                <NavigationMenu
                                    nav={nav}
                                    _class={conditionalClasses(
                                        checkProfessionalNav(
                                            nav.link,
                                            location.pathname
                                        ),
                                        'active',
                                        '',
                                        'hide-tablet-mobile'
                                    )}
                                    key={nav.key}
                                />
                            ))}
                        </ul>
                    </div>

                    <div className="find-a-hydrafacialist-button-container hide-tablet-mobile">
                        {isCustomerNavigation ? (
                            <Button
                                variant="contained"
                                className="find-a-hydrafacialist-button"
                                onClick={navigateToFindAHydrafacialist}
                            >
                                Find a Hydrafacialist
                            </Button>
                        ) : null}

                        {!isCareerPage && <CountrySelection />}
                    </div>
                </nav>
            </div>
            <nav
                className={conditionalClasses(
                    isMobileMenuOpen,
                    'opened',
                    '',
                    'mobile-nav hide-desktop'
                )}
            >
                <ul className="nav-ul">
                    {mobileNav?.map((colValue) =>
                        colValue.navs.length > 1 ? (
                            <Accordion
                                className="header-accordian"
                                key={colValue.heading}
                                expanded={expanded === colValue.heading}
                                onChange={handleChange(colValue.heading)}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <DownCarot className="down-carot" />
                                    }
                                    aria-controls="panel1a-content"
                                    id="panel1a-header"
                                >
                                    <div
                                        className="nav-head"
                                        id={colValue.click_id}
                                    >
                                        {colValue.heading}
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ul>
                                        {colValue.navs.map((nav: NavItem) => (
                                            <NavigationMenu
                                                nav={nav}
                                                key={nav.key}
                                                onClick={
                                                    handleMobileMenuOpenAccordion
                                                }
                                            />
                                        ))}
                                    </ul>
                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            <div
                                className="nav-head-container"
                                key={colValue.heading}
                            >
                                <div
                                    className="nav-head"
                                    id={colValue.click_id}
                                    onClick={() => {
                                        colValue.navs[0].link &&
                                            handleNavigate(
                                                colValue.navs[0].link
                                            )
                                    }}
                                >
                                    {colValue.heading}
                                </div>
                            </div>
                        )
                    )}
                    <div className="find-a-hydrafacialist-button-container">
                        <Button
                            variant="contained"
                            className="find-a-hydrafacialist-button"
                            onClick={navigateToFindAHydrafacialist}
                        >
                            Find a Hydrafacialist
                        </Button>
                    </div>
                    <div className="country-btn">
                        {!isCareerPage && <CountrySelection />}
                    </div>
                </ul>
                <div className="search-bar-container">
                    <SearchBar callback={setMobileMenuOpen} />
                </div>
            </nav>
        </header>
    )
}

export default Header
